import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import {
    Users,
    Menu,
    X,
    ArrowRight,
    Star,
    ChevronDown,
    UserPlus,
    Shield,
    AlertCircle,
    Zap,
    TrendingUp,
    Globe,
    Key,
    Sparkles,
    Flower2,
    Sun,
} from 'lucide-react';

// Импорт страниц
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';

import { authService } from './services/authService';

// НОВЫЙ ИМПОРТ: AuthContext
import { AuthProvider, useAuth } from './contexts/AuthContext';

// НОВЫЙ ИМПОРТ: Компонент рассылки
import BroadcastAdmin from './components/BroadcastAdmin';

// НОВЫЙ ИМПОРТ: Переключатель языка (Google Translate, стилизованный)
import LanguageSwitcher from './components/LanguageSwitcher';
import Nt from './components/Nt';

// ========== ВЕСЕННЕ-ЛЕТНИЕ КОМПОНЕНТЫ ==========

// Компонент падающих лепестков
const PetalFall = () => {
    const [petals, setPetals] = useState([]);

    useEffect(() => {
        const items = [];
        const petalEmojis = ['🌸', '🌺', '🌷', '🌼'];
        for (let i = 0; i < 40; i++) {
            items.push({
                id: i,
                emoji: petalEmojis[Math.floor(Math.random() * petalEmojis.length)],
                left: Math.random() * 100,
                animationDuration: 8 + Math.random() * 12,
                animationDelay: Math.random() * 8,
                size: 12 + Math.random() * 16,
                opacity: 0.4 + Math.random() * 0.5,
                sway: Math.random() * 40 - 20,
            });
        }
        setPetals(items);
    }, []);

    return (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
            {petals.map((petal) => (
                <div
                    key={petal.id}
                    className="absolute animate-petalfall"
                    style={{
                        left: `${petal.left}%`,
                        animationDuration: `${petal.animationDuration}s`,
                        animationDelay: `${petal.animationDelay}s`,
                        fontSize: `${petal.size}px`,
                        opacity: petal.opacity,
                        '--sway': `${petal.sway}px`,
                    }}
                >
                    {petal.emoji}
                </div>
            ))}
            <style>{`
                @keyframes petalfall {
                    0% {
                        transform: translateY(-10vh) translateX(0) rotate(0deg);
                    }
                    50% {
                        transform: translateY(50vh) translateX(var(--sway)) rotate(180deg);
                    }
                    100% {
                        transform: translateY(110vh) translateX(0) rotate(360deg);
                    }
                }
                .animate-petalfall {
                    animation: petalfall linear infinite;
                }
            `}</style>
        </div>
    );
};

// Летний баннер
const SeasonBanner = ({ onClose }) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const dismissed = sessionStorage.getItem('season_banner_dismissed');
        if (dismissed) {
            setIsVisible(false);
        }
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        sessionStorage.setItem('season_banner_dismissed', 'true');
        if (onClose) onClose();
    };

    if (!isVisible) return null;

    return (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-float-slow">
            <div className="bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-300 text-white px-8 py-4 rounded-2xl shadow-2xl border-4 border-white relative">
                <div className="absolute -top-3 -left-3 text-3xl">🌸</div>
                <div className="absolute -top-3 -right-3 text-3xl">🦋</div>
                <div className="absolute -bottom-3 -left-3 text-2xl">🌼</div>
                <div className="absolute -bottom-3 -right-3 text-2xl">☀️</div>

                <div className="text-center">
                    <h3 className="text-2xl font-bold mb-1">🌻 Привет, лето! 🌻</h3>
                    <p className="text-sm opacity-95">Желаем тёплых дней и ярких побед!</p>
                </div>

                <button
                    onClick={handleClose}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-white text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold hover:bg-gray-100 transition-colors"
                >
                    ×
                </button>
            </div>
            <style>{`
                @keyframes float-slow {
                    0%, 100% { transform: translate(-50%, 0); }
                    50% { transform: translate(-50%, -8px); }
                }
                .animate-float-slow {
                    animation: float-slow 3s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

// Летняя кнопка
const FestiveButton = ({ children, onClick, className = '' }) => {
    return (
        <button
            onClick={onClick}
            className={`relative overflow-hidden bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${className}`}
            style={{
                backgroundSize: '200% 100%',
                animation: 'gradient-shift 3s ease infinite',
            }}
        >
            <span className="relative z-10 flex items-center gap-2">
                ✨ {children} ✨
            </span>
            <style>{`
                @keyframes gradient-shift {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
        </button>
    );
};

// ========== КОНЕЦ ВЕСЕННЕ-ЛЕТНИХ КОМПОНЕНТОВ ==========

// Компонент для обработки различных типов реферальных ссылок
const ReferralHandler = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pathSegments = location.pathname.split('/').filter(Boolean);

    let refCode = null;

    refCode = params.get('ref') || params.get('referral') || params.get('invite');

    if (!refCode && pathSegments.length >= 2) {
        if (pathSegments[0] === 'ref' || pathSegments[0] === 'invite') {
            refCode = pathSegments[1];
        }
    }

    if (!refCode && pathSegments.length >= 2 && pathSegments[0] === 'r') {
        refCode = pathSegments[1];
    }

    if (refCode) {
        return <Navigate to={`/auth?ref=${refCode}`} replace />;
    }

    return <InviteInfoPage />;
};

// Страница с информацией о том, как получить приглашение (с весенне-летним оформлением)
const InviteInfoPage = () => {
    const petalEmojis = ['🌸', '🌺', '🌼', '🌷'];
    return (
        <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Лепестки на фоне */}
            <div className="absolute inset-0 pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute opacity-40 animate-petalfall-invite"
                        style={{
                            left: `${Math.random() * 100}%`,
                            animationDuration: `${8 + Math.random() * 10}s`,
                            animationDelay: `${Math.random() * 5}s`,
                            fontSize: `${12 + Math.random() * 12}px`,
                        }}
                    >
                        {petalEmojis[i % petalEmojis.length]}
                    </div>
                ))}
            </div>

            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center relative border-2 border-emerald-200">
                {/* Летние декорации */}
                <div className="absolute -top-4 -left-4 text-4xl">🌸</div>
                <div className="absolute -top-4 -right-4 text-4xl">🦋</div>

                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-sky-100 to-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-4xl">🌼</span>
                </div>

                <h1 className="text-2xl font-bold text-gray-800 mb-4">
                    🌸 Нужно приглашение 🌸
                </h1>

                <p className="text-gray-600 mb-6">
                    Эта ссылка недействительна. Для регистрации в ЛАЙТ необходима персональная реферальная ссылка от действующего участника.
                </p>

                <div className="space-y-3">
                    <Link to="/" className="block">
                        <button className="w-full bg-gradient-to-r from-sky-400 to-emerald-400 text-white py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium">
                            🏠 На главную страницу
                        </button>
                    </Link>

                    <Link to="/auth" className="block">
                        <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                            У меня уже есть аккаунт
                        </button>
                    </Link>
                </div>
            </div>

            <style>{`
                @keyframes petalfall-invite {
                    0% { transform: translateY(-10vh) rotate(0deg); }
                    100% { transform: translateY(110vh) rotate(360deg); }
                }
                .animate-petalfall-invite {
                    animation: petalfall-invite linear infinite;
                }
            `}</style>
        </div>
    );
};

// ✅ ИСПРАВЛЕНО: AdminRoute использует useAuth вместо запроса
const AdminRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">🌸</div>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                </div>
            </div>
        );
    }

    const isAdmin = user?.is_admin ||
        user?.role === 'admin' ||
        user?.login?.toLowerCase() === 'admin' ||
        user?.login === 'Admin';

    if (!isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
                <div className="text-center">
                    <div className="text-6xl mb-4">🦋</div>
                    <AlertCircle className="mx-auto text-rose-500 mb-4" size={48} />
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Доступ запрещён</h1>
                    <p className="text-gray-600 mb-2">У вас нет прав для доступа к админ-панели</p>
                    <p className="text-sm text-gray-500 mb-6">Ваш логин: {user?.login || 'не определен'}</p>
                    <Link to="/dashboard">
                        <button className="bg-gradient-to-r from-sky-400 to-emerald-400 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all">
                            🏠 Вернуться в кабинет
                        </button>
                    </Link>
                </div>
            </div>
        );
    }

    return children;
};

// ✅ ИСПРАВЛЕНО: ProtectedRoute использует useAuth вместо запроса
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">🌻</div>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загружаем летнюю атмосферу...</p>
                </div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/auth" replace />;
};

// ✅ ВЕСЕННЕ-ЛЕТНЯЯ ВЕРСИЯ HomePage
const HomePage = ({ showJoinModal, setShowJoinModal }) => {
    const { isAuthenticated, user } = useAuth();
    const [counter, setCounter] = useState(3500);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showPetals, setShowPetals] = useState(true);
    const [showBanner, setShowBanner] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setCounter(prev => prev + Math.floor(Math.random() * 3) + 1);
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 text-gray-800 w-full max-w-full relative">

            {/* 🌸 ВЕСЕННЕ-ЛЕТНИЕ ЭФФЕКТЫ 🌸 */}
            {showPetals && <PetalFall />}
            {showBanner && <SeasonBanner onClose={() => setShowBanner(false)} />}

            {/* Кнопка вкл/выкл лепестков */}
            <button
                onClick={() => setShowPetals(!showPetals)}
                className="fixed bottom-4 right-4 z-50 bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all border-2 border-emerald-200"
                title={showPetals ? "Выключить лепестки" : "Включить лепестки"}
            >
                {showPetals ? "🌸" : "🌿"}
            </button>

            {/* Декоративные элементы */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-sky-50/20 to-amber-50/20 -z-10"></div>

            {/* 🌸 ЛЕТНИЙ ХЕДЕР 🌸 */}
            <header className="relative z-40 bg-white/90 backdrop-blur-md shadow-sm sticky top-0">
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400"></div>
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center py-4">
                        {/* Логотип с летним украшением */}
                        <div className="flex items-center space-x-6">
                            <Link to="/" className="flex items-center space-x-3 relative">
                                {/* Цветок на логотипе */}
                                <div className="absolute -top-3 -left-2 text-xl transform -rotate-12 z-10">🌸</div>

                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center shadow-lg border-2 border-emerald-200">
                                    <img
                                        src="/images/logo.png"
                                        alt="ЛАЙТ логотип"
                                        className="w-8 h-8 object-contain"
                                    />
                                </div>
                                <span className="text-2xl font-bold bg-gradient-to-r from-sky-500 to-emerald-500 bg-clip-text text-transparent">ЛАЙТ</span>
                                <span className="text-lg">🌿</span>
                            </Link>

                            {/* Счётчик с летним оформлением */}
                            <div className="hidden md:flex items-center bg-gradient-to-r from-sky-100 to-emerald-100 px-4 py-2 rounded-full border border-emerald-200">
                                <span className="mr-2">🌼</span>
                                <Users size={16} className="text-emerald-600 mr-2"/>
                                <span className="text-emerald-700 font-semibold text-sm">{counter.toLocaleString()}+ участников</span>
                                <span className="ml-2">🌸</span>
                            </div>
                        </div>

                        {/* Кнопки авторизации с летним стилем */}
                        <div className="hidden md:flex items-center space-x-4">
                            {/* Переключатель языка */}
                            <LanguageSwitcher />
                            {isAuthenticated ? (
                                <div className="flex items-center space-x-3">
                                    {user && (
                                        <div className="flex items-center relative">
                                            <div className="absolute -top-2 -right-1 text-sm z-10">🦋</div>
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-sm mr-2 border border-emerald-200">
                                                {user.avatar ? (
                                                    <img src={user.avatar} alt={user.login} className="w-8 h-8 rounded-full object-cover" />
                                                ) : (
                                                    user.login.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <span className="text-sm font-medium text-gray-700"><Nt>{user.login}</Nt></span>
                                        </div>
                                    )}
                                    <Link to="/dashboard">
                                        <FestiveButton>
                                            Личный кабинет
                                        </FestiveButton>
                                    </Link>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-3">
                                    <Link to="/auth">
                                        <button className="text-emerald-600 hover:text-emerald-700 transition-colors px-4 py-2 rounded-lg hover:bg-emerald-50">
                                            Войти
                                        </button>
                                    </Link>
                                    <FestiveButton onClick={() => setShowJoinModal(true)}>
                                        Присоединиться
                                    </FestiveButton>
                                </div>
                            )}
                        </div>

                        {/* Мобильное меню кнопка */}
                        <div className="md:hidden flex items-center space-x-2">
                            <LanguageSwitcher />
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="text-emerald-600 p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                                {isMenuOpen ? <X size={28}/> : <Menu size={28}/>}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Мобильное меню с летним оформлением */}
            <div
                className={`fixed top-0 right-0 h-screen bg-gradient-to-b from-white to-sky-50 w-64 z-50 transform transition-transform duration-300 pt-20 px-6 shadow-2xl ${
                    isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="absolute top-4 right-4">
                    <button onClick={() => setIsMenuOpen(false)} className="text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="text-center mb-6">
                    <span className="text-4xl">🌻</span>
                </div>

                <div className="flex items-center mb-8 bg-gradient-to-r from-sky-100 to-emerald-100 p-3 rounded-lg border border-emerald-200">
                    <span className="mr-2">🌼</span>
                    <Users size={18} className="text-emerald-600 mr-2"/>
                    <span className="text-emerald-700 font-medium">{counter}+ участников</span>
                </div>

                {isAuthenticated ? (
                    <div className="mt-8 space-y-3">
                        {user && (
                            <div className="flex items-center p-3 bg-white rounded-lg border border-emerald-200">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-emerald-600 font-semibold mr-3">
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.login} className="w-10 h-10 rounded-full object-cover" />
                                    ) : (
                                        user.login.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium text-gray-800"><Nt>{user.login}</Nt></p>
                                </div>
                            </div>
                        )}
                        <Link to="/dashboard" className="w-full" onClick={() => setIsMenuOpen(false)}>
                            <button className="w-full bg-gradient-to-r from-sky-400 to-emerald-400 text-white py-3 px-6 rounded-full hover:shadow-lg transition-all duration-300 font-medium">
                                🌸 Личный кабинет 🌼
                            </button>
                        </Link>
                    </div>
                ) : (
                    <div className="mt-8 space-y-3">
                        <Link to="/auth" className="block" onClick={() => setIsMenuOpen(false)}>
                            <button className="w-full text-emerald-600 border-2 border-emerald-400 py-3 px-6 rounded-full hover:bg-emerald-50 transition-all duration-300 font-medium">
                                Войти
                            </button>
                        </Link>
                        <button
                            onClick={() => {
                                setShowJoinModal(true);
                                setIsMenuOpen(false);
                            }}
                            className="w-full bg-gradient-to-r from-sky-400 to-emerald-400 text-white py-3 px-6 rounded-full hover:shadow-lg transition-all duration-300 font-medium"
                        >
                            🌸 Присоединиться 🌼
                        </button>
                    </div>
                )}
            </div>

            {/* Летнее модальное окно для присоединения */}
            {showJoinModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border-4 border-emerald-200 relative">
                        {/* Летние декорации */}
                        <div className="absolute -top-4 -left-4 text-4xl z-10">🌸</div>
                        <div className="absolute -top-4 -right-4 text-4xl z-10">🦋</div>

                        {/* Заголовок */}
                        <div className="bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400 text-white p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <span className="text-2xl mr-3">🌼</span>
                                    <h3 className="text-xl font-semibold">Присоединиться к ЛАЙТ</h3>
                                </div>
                                <button
                                    onClick={() => setShowJoinModal(false)}
                                    className="text-white/80 hover:text-white transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Контент */}
                        <div className="p-6">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-sky-100 to-emerald-100 rounded-full flex items-center justify-center">
                                    <span className="text-3xl">🌼</span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                    🌸 Регистрация только по приглашениям 🌸
                                </h4>
                                <p className="text-gray-600 text-sm">
                                    Платформа ЛАЙТ работает как закрытое сообщество. Чтобы присоединиться,
                                    вам необходимо получить персональную ссылку от действующего участника.
                                </p>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-sky-600 text-sm font-bold">1</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">🔍 Найдите участника</p>
                                        <p className="text-xs text-gray-600">Свяжитесь с знакомым, который уже является участником ЛАЙТ</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-emerald-600 text-sm font-bold">2</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">🔗 Получите ссылку</p>
                                        <p className="text-xs text-gray-600">Попросите личную реферальную ссылку для регистрации</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-amber-600 text-sm font-bold">3</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800">✅ Зарегистрируйтесь</p>
                                        <p className="text-xs text-gray-600">Перейдите по ссылке и создайте аккаунт</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-sky-50 to-emerald-50 rounded-lg p-4 mb-6 border border-emerald-200">
                                <div className="flex items-center mb-2">
                                    <Shield className="text-emerald-600 mr-2" size={18} />
                                    <span className="text-sm font-medium text-gray-800">Почему так?</span>
                                </div>
                                <p className="text-xs text-gray-600">
                                    🌿 Это обеспечивает качество сообщества и позволяет поддерживать доверительные отношения между участниками.
                                </p>
                            </div>
                        </div>

                        {/* Кнопки */}
                        <div className="p-6 pt-0 space-y-3">
                            <Link to="/auth" className="block">
                                <button
                                    onClick={() => setShowJoinModal(false)}
                                    className="w-full bg-gradient-to-r from-sky-400 to-emerald-400 text-white py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium"
                                >
                                    🌼 У меня есть ссылка 🌸
                                </button>
                            </Link>
                            <button
                                onClick={() => setShowJoinModal(false)}
                                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Понятно
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Основной контент главной страницы */}
            <main className="pt-8 pb-20">
                {/* 🌸 ЛЕТНИЙ Hero секция 🌸 */}
                <section id="home" className="container mx-auto px-4 flex flex-col-reverse md:flex-row items-center">
                    <div className="md:w-1/2 mt-12 md:mt-0">
                        {/* Летний бейдж */}
                        <div className="bg-gradient-to-r from-sky-100 via-emerald-100 to-amber-100 px-4 py-2 rounded-full inline-flex items-center mb-4 border border-emerald-200 shadow-lg">
                            <span className="mr-2">🌸</span>
                            <Zap size={16} className="text-emerald-600 mr-2"/>
                            <span className="text-emerald-700 font-medium">Новык Возможности!</span>
                            <span className="ml-2">🌼</span>
                        </div>

                        {/* Заголовок с летним оформлением */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                            <span className="bg-gradient-to-r from-sky-500 via-emerald-500 to-amber-500 bg-clip-text text-transparent">
                                🌻 Привет, лето!
                            </span>
                            <br/>
                            <span className="bg-gradient-to-r from-blue-primary to-blue-primary/70 bg-clip-text text-transparent">
                                Откройте новые
                            </span>
                            <br/>
                            <span className="text-gray-800">
                                горизонты успеха ☀️
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl mb-8 max-w-md text-gray-700">
                            🦋 Присоединяйтесь к нашему сообществу этим летом!
                            Особые условия для новых партнёров! 🌼
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/auth">
                                <FestiveButton className="text-lg py-4 px-12">
                                    🌸 Начать сейчас 🌼
                                </FestiveButton>
                            </Link>
                        </div>

                        {/* Статистика с летним оформлением */}
                        <div className="mt-12 grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl border-2 border-sky-200 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="text-sky-600 font-bold text-3xl">{counter}+</div>
                                <div className="text-gray-600 text-sm">🌸 Участников</div>
                            </div>
                            <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border-2 border-emerald-200 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="text-emerald-600 font-bold text-3xl">96%</div>
                                <div className="text-gray-600 text-sm">⭐ Довольных</div>
                            </div>
                            <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border-2 border-amber-200 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="text-amber-600 font-bold text-3xl">24/7</div>
                                <div className="text-gray-600 text-sm">🌼 Поддержка</div>
                            </div>
                        </div>
                    </div>

                    {/* Правая часть Hero секции — солнце и орбиты */}
                    <div className="md:w-1/2 flex justify-center">
                        <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96">
                            {/* Летний фон */}
                            <div className="absolute inset-0 bg-gradient-to-br from-sky-200/50 via-emerald-200/50 to-amber-200/50 rounded-full opacity-70 animate-pulse blur-lg"></div>

                            {/* Солнце в центре */}
                            <div className="absolute inset-8 bg-white rounded-full shadow-xl flex items-center justify-center overflow-hidden border-4 border-amber-200">
                                <div className="text-8xl" style={{ animation: 'sun-glow 2s ease-in-out infinite' }}>☀️</div>
                            </div>

                            {/* Орбитальные летние элементы */}
                            <div className="absolute w-full h-full animate-spin-slow">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-3xl border-2 border-pink-200">
                                    🌸
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slower" style={{animationDelay: "0.5s"}}>
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-3xl border-2 border-emerald-200">
                                    🦋
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slowest" style={{animationDelay: "1s"}}>
                                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-3xl border-2 border-amber-200">
                                    🌼
                                </div>
                            </div>

                            <div className="absolute w-full h-full animate-spin-slower" style={{animationDelay: "1.5s"}}>
                                <div className="absolute top-1/2 right-0 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center text-3xl border-2 border-sky-200">
                                    🐝
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* 🌸 ЛЕТНИЙ ПОДВАЛ 🌸 */}
            <footer className="bg-gradient-to-r from-sky-500 via-emerald-500 to-amber-500 text-white py-12 relative overflow-hidden">
                {/* Лепестки в футере */}
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    {[...Array(15)].map((_, i) => (
                        <span
                            key={i}
                            className="absolute text-4xl animate-petalfall-footer"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 5}s`,
                                animationDuration: `${8 + Math.random() * 5}s`
                            }}
                        >
                            🌸
                        </span>
                    ))}
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="flex flex-col md:flex-row justify-between">
                        <div className="mb-8 md:mb-0">
                            <div className="flex items-center mb-6">
                                <span className="text-3xl mr-2">🌻</span>
                                <div className="w-10 h-10 rounded-full overflow-hidden mr-3 bg-white flex items-center justify-center">
                                    <span className="text-emerald-700 font-bold">Л</span>
                                </div>
                                <span className="text-xl font-medium tracking-wider">ЛАЙТ</span>
                                <span className="text-3xl ml-2">🦋</span>
                            </div>
                            <p className="max-w-xs">
                                🌼 Хорошего лета! Желаем успехов и процветания всем нашим партнёрам! ☀️
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                            <div>
                                <h4 className="text-lg font-medium mb-4 flex items-center">
                                    <span className="mr-2">🏢</span> Компания
                                </h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">О нас</a></li>
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">Команда</a></li>
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">Карьера</a></li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="text-lg font-medium mb-4 flex items-center">
                                    <span className="mr-2">🤝</span> Партнёрство
                                </h4>
                                <ul className="space-y-2">
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">Возможности</a></li>
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">Обучение</a></li>
                                    <li><a href="#" className="hover:text-amber-200 transition-colors">FAQ</a></li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="text-lg font-medium mb-4 flex items-center">
                                    <span className="mr-2">📞</span> Контакты
                                </h4>
                                <ul className="space-y-2">
                                    <li>info@лайт.рф</li>
                                    <li>+7 (123) 456-78-90</li>
                                    <li>Москва</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-white/20 mt-12 pt-6 flex flex-col md:flex-row justify-between items-center">
                        <p>© 2026 ЛАЙТ. 🌸 Хорошего лета! 🌼 Все права защищены.</p>
                        <div className="mt-4 md:mt-0">
                            <ul className="flex space-x-6">
                                <li><a href="/politiks_and_rules.pdf" className="hover:text-amber-200 transition-colors">Политика конфиденциальности</a></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Дополнительные стили для анимаций */}
            <style>{`
                @keyframes sun-glow {
                    0%, 100% { filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.5)); }
                    50% { filter: drop-shadow(0 0 25px rgba(251, 191, 36, 0.9)); }
                }
                @keyframes petalfall-footer {
                    0% { transform: translateY(-10vh) rotate(0deg); }
                    100% { transform: translateY(110vh) rotate(360deg); }
                }
                .animate-petalfall-footer {
                    animation: petalfall-footer linear infinite;
                }
            `}</style>
        </div>
    );
};

// ✅ ИСПРАВЛЕНО: AppContent использует useAuth
function AppContent() {
    const { login, logout, refreshUser } = useAuth();
    const [showJoinModal, setShowJoinModal] = useState(false);

    const handleLogin = async () => {
        await refreshUser();
    };

    const handleLogout = () => {
        logout();
    };

    return (
        <Routes>
            {/* Главная страница */}
            <Route path="/" element={
                <HomePage
                    showJoinModal={showJoinModal}
                    setShowJoinModal={setShowJoinModal}
                />
            } />

            {/* Обработка всех типов реферальных ссылок */}
            <Route path="/ref/:refCode" element={<ReferralHandler />} />
            <Route path="/invite/:refCode" element={<ReferralHandler />} />
            <Route path="/invite" element={<ReferralHandler />} />
            <Route path="/r/:refCode" element={<ReferralHandler />} />

            {/* Авторизация/регистрация */}
            <Route path="/auth" element={<AuthPage onLogin={handleLogin} />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Админ-рассылка */}
            <Route path="/admin/broadcast" element={
                <ProtectedRoute>
                    <AdminRoute>
                        <BroadcastAdmin />
                    </AdminRoute>
                </ProtectedRoute>
            } />

            {/* Защищённые маршруты */}
            <Route path="/dashboard/*" element={
                <ProtectedRoute>
                    <Dashboard onLogout={handleLogout} />
                </ProtectedRoute>
            } />

            {/* 404 и неправильные ссылки */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// ✅ ГЛАВНЫЙ КОМПОНЕНТ: Оборачиваем всё в AuthProvider
function App() {
    useEffect(() => {
        const checkVersion = async () => {
            try {
                const response = await fetch('/meta.json?' + new Date().getTime());
                const data = await response.json();
                const currentVersion = localStorage.getItem('app_version');

                if (currentVersion && currentVersion !== data.version) {
                    console.log('Обнаружена новая версия, обновление...');
                    localStorage.setItem('app_version', data.version);
                    window.location.reload(true);
                } else if (!currentVersion) {
                    localStorage.setItem('app_version', data.version);
                }
            } catch (error) {
                console.error('Ошибка проверки версии:', error);
            }
        };

        checkVersion();
    }, []);

    return (
        <Router>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </Router>
    );
}

export default App;
